import { createDsSocket, SocketLike } from "./ds-socket";
import { ON_PORTAL } from "../shared/portal-env";

// The online backend is the Cloudflare Durable Objects edge-server (the legacy Render
// socket.io server has been retired). Point at a different instance with ?server= or
// VITE_DS_SERVER_URL.
const DS_PRODUCTION_SERVER_URL = "wss://achtung-edge-server.ommatte.workers.dev";

// Resolve the edge-server origin (ws/wss). Order: ?server= override wins, then
// VITE_DS_SERVER_URL, then a game site's build (hosted by the site: always the
// real server), then localhost wrangler dev (:8787), then the Cloudflare-hosted
// site (prod or a Pages preview - static only, so reach the worker), else same origin.
function getDsOrigin(override?: string | null): string {
  if (override) return override;
  const env = (import.meta as any).env;
  if (env && env.VITE_DS_SERVER_URL) return env.VITE_DS_SERVER_URL;
  if (ON_PORTAL) return DS_PRODUCTION_SERVER_URL;
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") return "ws://localhost:8787";
    if (host === "achtungroyale.io" || host === "www.achtungroyale.io" || host.endsWith(".pages.dev")) {
      return DS_PRODUCTION_SERVER_URL;
    }
    const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${wsProto}//${window.location.host}`;
  }
  return "ws://localhost:8787";
}

// A stable per-device identity, sent with `play`. The server keys disconnected seats
// by it, so a browser reload (same device) can reclaim its seat in a running match.
export function getClientId(): string {
  try {
    let id = localStorage.getItem("achtungClientId");
    if (!id) {
      id = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("achtungClientId", id);
    }
    return id;
  } catch {
    return "anon-" + Math.random().toString(36).slice(2);
  }
}

// This page load's id, sent with `play` beside the clientId: a reconnect from the
// same page tells the server its old connection is gone, so the seat is taken
// over (a second tab on the same device has another and keeps its own seat).
const TAB_ID = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
export function getTabId(): string {
  return TAB_ID;
}

// The concrete room instance this TAB is (or was just) playing in, per requested room.
// sessionStorage so it survives a reload but stays per-tab: a reload must reconnect to
// the SAME instance (a public base would otherwise re-matchmake into the freshly rolled
// open box, and a solo room name is random per connection). Cleared on a deliberate
// leave / bounce so the next join matchmakes fresh.
function stickyKey(room?: string): string { return "achtungInstance:" + (room || "solo"); }
export function clearStickyRoom(room?: string): void {
  try {
    // ... and forget which worker it lived on (blue/green, see roomOrigin)
    const held = sessionStorage.getItem(stickyKey(room));
    if (held) sessionStorage.removeItem("achtungOrigin:" + held);
    if (room) sessionStorage.removeItem("achtungOrigin:" + dsRoomName(room));
    sessionStorage.removeItem(stickyKey(room));
    // A concrete instance ("public:small#951", from a match-id URL) also releases its
    // BASE room's sticky: a deliberate leave means "done with that match", and a held
    // base sticky would pull the next join of that box straight back into it.
    const hash = room ? room.indexOf("#") : -1;
    if (hash > 0) sessionStorage.removeItem(stickyKey(room!.slice(0, hash)));
  } catch { /* no storage */ }
}
// This tab's sticky instance (and the worker it lives on) for a room, to carry
// it across an app restart (online/resume.ts), and to put it back.
export function stickyFor(room?: string): { instance: string | null; origin: string | null } {
  try {
    const instance = sessionStorage.getItem(stickyKey(room));
    return { instance, origin: instance ? sessionStorage.getItem("achtungOrigin:" + instance) : null };
  } catch { return { instance: null, origin: null }; }
}
export function restoreSticky(room: string | undefined, instance: string | null, origin: string | null): void {
  try {
    if (instance) sessionStorage.setItem(stickyKey(room), instance);
    if (instance && origin) sessionStorage.setItem("achtungOrigin:" + instance, origin);
  } catch { /* no storage */ }
}
// The room this tab was sent to turned it away (full, closed, too late to join):
// forget it, and never take it from the matchmaker again in this page's life.
const refusedRooms = new Set<string>();
export function refuseStickyRoom(room?: string): void {
  try {
    const held = sessionStorage.getItem(stickyKey(room));
    if (held) refusedRooms.add(held);
  } catch { /* no storage */ }
  clearStickyRoom(room);
}
export function hasStickyRoom(room?: string): boolean {
  try { return sessionStorage.getItem(stickyKey(room)) !== null; } catch { return false; }
}

// Map the lobby `room` value to a MatchRoom DO instance name. Named rooms share a DO
// ("room:NAME"); public boxes keep their base ("public:small", resolved to an instance
// via the matchmaker); solo gets a private per-tab DO (sticky across reloads, see
// above) so two solo players never collide.
function dsRoomName(room?: string): string {
  if (!room) {
    try {
      const key = stickyKey(undefined);
      let r = sessionStorage.getItem(key);
      if (!r) { r = "solo:" + Math.random().toString(36).slice(2, 10); sessionStorage.setItem(key, r); }
      return r;
    } catch {
      return "solo:" + Math.random().toString(36).slice(2, 10);
    }
  }
  if (room.startsWith("public:") || room.startsWith("room:") || room.startsWith("solo:") || room.startsWith("lobby:")) return room;
  return "room:" + room;
}

// Blue/green: matches run on two match workers, and a room lives on one of them
// for life (edge-server server.js, COLORS). Before connecting, the client asks the
// front (the address above) where the room lives and connects straight there, so
// a deploy of the front never cuts a match. The answer sticks per tab with the
// room, so a reload reconnects to the same worker. No address in the answer (an
// older server, local dev): connect to the front, which hands the connection on.
const originKey = (instance: string) => "achtungOrigin:" + instance;
async function roomOrigin(override: string | null | undefined, instance: string, known?: string | null): Promise<string> {
  let origin: string | null = known ?? null;
  if (!origin) {
    try { origin = sessionStorage.getItem(originKey(instance)); } catch { /* no storage */ }
  }
  if (!origin) {
    try {
      const res = await fetch(`${dsHttpOrigin(override)}/route?room=${encodeURIComponent(instance)}`);
      if (res.ok) origin = (await res.json()).origin || null;
    } catch { /* an older server: no /route */ }
  }
  if (origin) { try { sessionStorage.setItem(originKey(instance), origin); } catch { /* no storage */ } }
  return origin || getDsOrigin(override);
}

function dsResolver(override: string | null | undefined, room?: string): () => Promise<string> {
  return async () => {
    const instance = dsRoomName(room);
    return `${await roomOrigin(override, instance)}/parties/match_room/${encodeURIComponent(instance)}`;
  };
}

// ws(s):// -> http(s):// for the matchmaker fetch. Exported for the shop API
// (its Stripe routes live on the same game-server Worker).
export function dsHttpOrigin(override?: string | null): string {
  return getDsOrigin(override).replace(/^ws/, "http");
}

// Public boxes go through the matchmaker first: ask which instance is currently open,
// then connect to that MatchRoom DO. Returns an async resolver for the shim. The
// resolved instance sticks per-tab (sessionStorage) so a reload reconnects to the
// SAME match instead of re-matchmaking into the next open box; clearStickyRoom (on
// leave / bounce) is what releases it.
function dsPublicResolver(override: string | null | undefined, room: string, onInstance?: (instance: string) => void): () => Promise<string> {
  return async () => {
    const key = stickyKey(room);
    let instance: string | null = null;
    let origin: string | null = null;
    try { instance = sessionStorage.getItem(key); } catch { /* no storage */ }
    if (!instance) {
      const res = await fetch(`${dsHttpOrigin(override)}/matchmake?room=${encodeURIComponent(room)}`);
      const j = await res.json();
      instance = j.room || room;
      origin = j.origin || null; // the worker the box lives on (blue/green)
      // A Royale that started moments ago may still take us (its first rounds):
      // try it first, unless it already turned us away (refuseStickyRoom).
      if (j.late && !refusedRooms.has(j.late)) { instance = j.late; origin = j.lateOrigin || null; }
      try { sessionStorage.setItem(key, instance); } catch { /* no storage */ }
    }
    // Surface the concrete instance (e.g. "public:small#951") so the page can put the
    // match id in the URL - the strongest reload/share anchor for the session.
    if (onInstance) { try { onInstance(instance!); } catch { /* page callback */ } }
    return `${await roomOrigin(override, instance!, origin)}/parties/match_room/${encodeURIComponent(instance!)}`;
  };
}

// A map as the server describes it (edge-server map-desc.js). The standard arenas
// are the 2:1 Rectangle and the 2:1 Stadium (a pill lying along x).
export type ServerMapDesc =
  | { type: "Square"; size: number }
  | { type: "Circle"; radius: number }
  | { type: "Rectangle"; width: number; height: number }
  | { type: "Stadium"; width: number; height: number };

// Options the client offers the server when starting a match. All optional; the server
// fills sensible defaults (1 bot, the standard 2:1 rectangle map, first-to maxScore).
export interface PlayOptions {
  name?: string;
  colorId?: string;
  botCount?: number;
  maxScore?: number;
  map?: ServerMapDesc;
  // custom-lobby config (only the host's first `play` is honored by the server)
  maxPlayers?: number;
  botFill?: boolean;
  public?: boolean;
  // full match rules a custom-lobby host configures (honored on the host's first play)
  maxRounds?: number;
  disabledPowerUps?: string[];
  powerUpSpawnChance?: number;
  wormSpeed?: number;
  botFillSkill?: number; // difficulty (1-10) for auto-filled bots
  respawn?: boolean;     // Respawn rounds (core respawn.js)
}

// A lobby chat line: a player message (name + colour) or a system line ("X joined").
export interface ChatMessage {
  name?: string;
  colorId?: string | null;
  text: string;
  system?: boolean;
}

// Connect to the edge-server. Returns a socket.io-compatible shim over partysocket;
// `room` selects the MatchRoom DO instance. A public BASE room ("public:small")
// resolves via the matchmaker (reporting the concrete instance through `onInstance`);
// a room that already names an instance ("public:small#951", from a match URL)
// connects straight to it.
// A debug page can stand in for the server (the timings lab runs a room in the
// browser): while one is set, games connect to it instead. Never set in play.
type LabServer = (room: string) => SocketLike;
let labServer: LabServer | null = null;
export function setLabServer(fn: LabServer | null): void {
  labServer = fn;
}

export function connectToServer(urlOverride?: string | null, room?: string, onInstance?: (instance: string) => void): SocketLike {
  if (labServer) return labServer(room || "");
  const target = room && room.startsWith("public:") && room.indexOf("#") === -1
    ? dsPublicResolver(urlOverride, room, onInstance)
    : dsResolver(urlOverride, room);
  return createDsSocket(target);
}

// Live player counts for the lobby's matchmaking boxes, keyed by size base
// ("small" | "medium" | "large"). `players` is everyone seated in the open room.
export interface LobbyCount {
  players: number;
  capacity: number;
  startAt: number;
  // The map this box's open instance will actually play (varies per cycle), so the
  // card preview can match. null only briefly before the first instance opens.
  map?: ServerMapDesc | null;
  // This cycle's playstyle name (e.g. "Sabotage"), for the card's chip + preview.
  playstyle?: string | null;
  // The Royale's box only: its running number ("Royale #214") and who won the last one.
  number?: number;
  lastWinner?: { number: number; name: string; flag: string | null } | null;
}
export type LobbyCounts = Record<string, LobbyCount>;

// How long a hidden tab keeps its lobby socket, so a quick tab switch doesn't reconnect.
const HIDDEN_GRACE_MS = 15000;

// Keep a lobby socket open only while the tab is visible (after a short grace once it
// is hidden). A forgotten background tab otherwise holds the Lobby DO open around the
// clock, which Cloudflare bills. `open` connects and returns its disconnect; it runs
// again on return, and the fresh subscribe brings the view up to date.
function whileVisible(open: () => () => void): () => void {
  if (typeof document === "undefined") return open();
  let close: (() => void) | null = document.hidden ? null : open();
  let graceTimer: ReturnType<typeof setTimeout> | null = null;
  const onVisibility = () => {
    if (graceTimer) { clearTimeout(graceTimer); graceTimer = null; }
    if (!document.hidden) { if (!close) close = open(); return; }
    graceTimer = setTimeout(() => { graceTimer = null; close?.(); close = null; }, HIDDEN_GRACE_MS);
  };
  document.addEventListener("visibilitychange", onVisibility);
  return () => {
    document.removeEventListener("visibilitychange", onVisibility);
    if (graceTimer) clearTimeout(graceTimer);
    close?.();
    close = null;
  };
}

// Subscribe to the matchmaking boxes' live counts via the Lobby coordinator DO (it
// pushes { type:"lobby_counts", counts }). Returns a cleanup that disconnects it.
// The last counts and lobby list the Lobby sent, kept for the next screen that
// shows them: a start page you come back to draws its cards (the Royale's
// players and last winner, the open rooms) at once, not half a second later
// when its own connection catches up (the card visibly settled as they arrived).
let lastCounts: LobbyCounts | null = null;
let lastLobbies: CustomLobby[] | null = null;
export const peekLobbyCounts = (): LobbyCounts | null => lastCounts;
export const peekCustomLobbies = (): CustomLobby[] | null => lastLobbies;

export function subscribeLobbyCounts(onCounts: (c: LobbyCounts) => void, urlOverride?: string | null): () => void {
  if (lastCounts && !urlOverride) onCounts(lastCounts);
  return whileVisible(() => {
    const socket = createDsSocket(`${getDsOrigin(urlOverride)}/parties/lobby/main`);
    socket.on("connect", () => socket.emit("lobby_subscribe"));
    socket.on("lobby_counts", (m: any) => { const c = (m && m.counts) || {}; if (!urlOverride) lastCounts = c; onCounts(c); });
    return () => { socket.removeAllListeners(); socket.disconnect(); };
  });
}

// One open, joinable public custom lobby shown in the Join screen's browse list.
export interface CustomLobby {
  code: string;
  hostName: string;
  name?: string | null; // what the host called the room
  map?: ServerMapDesc | null;
  players: number;
  maxPlayers: number;
}

// Subscribe to the browsable list of open public custom lobbies via the Lobby DO (it
// pushes { type:"custom_lobbies", lobbies }). Returns a cleanup that disconnects it.
export function subscribeCustomLobbies(onLobbies: (l: CustomLobby[]) => void, urlOverride?: string | null): () => void {
  if (lastLobbies && !urlOverride) onLobbies(lastLobbies);
  return whileVisible(() => {
    const socket = createDsSocket(`${getDsOrigin(urlOverride)}/parties/lobby/main`);
    socket.on("connect", () => socket.emit("custom_subscribe"));
    socket.on("custom_lobbies", (m: any) => { const l = (m && m.lobbies) || []; if (!urlOverride) lastLobbies = l; onLobbies(l); });
    return () => { socket.removeAllListeners(); socket.disconnect(); };
  });
}

// The Play Now room someone joining right now would land in (the fullest one with
// a seat), without joining or opening one: null when no room is running.
export async function peekPlayNow(urlOverride?: string | null): Promise<{ room: string; origin: string | null } | null> {
  try {
    const res = await fetch(`${dsHttpOrigin(urlOverride)}/matchmake?room=${encodeURIComponent("public:playnow")}&peek=1`);
    const j = await res.json();
    return j && j.room ? { room: j.room, origin: j.origin || null } : null;
  } catch {
    return null;
  }
}

// Watch a running room without a seat (the start page's live window into the
// Play Now room): the server streams it the match like any player, and says
// "room_closed" when there is nothing (left) to watch.
export function watchRoom(room: string, origin: string | null, urlOverride?: string | null): SocketLike {
  const socket = createDsSocket(`${origin || getDsOrigin(urlOverride)}/parties/match_room/${encodeURIComponent(room)}`);
  socket.on("connect", () => socket.emit("watch", { wire: 3 }));
  return socket;
}
